import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Button,
  useTheme,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

// Keyframe animations
const floatingAnimation = `
  @keyframes floatSlow {
    0%, 100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(20px, -20px);
    }
  }
`;

const glowPulse = `
  @keyframes glowPulse {
    0%, 100% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.8;
    }
  }
`;

const DashboardPreview = () => {
  const theme = useTheme();

  const metrics = [
    {
      label: "Total Staff",
      value: "47",
      change: "+5%",
      positive: true,
    },
    {
      label: "Active Now",
      value: "24",
      subtitle: "51% utilization",
    },
    {
      label: "Avg. Check-in",
      value: "8:54 AM",
      change: "-3 min",
      positive: true,
    },
    {
      label: "Overtime Hours",
      value: "12.5",
      change: "-2 from last week",
      positive: true,
    },
  ];

  const staffActivity = [
    {
      name: "Sarah Johnson",
      status: "On Time",
      statusColor: "success",
      location: "Downtown Office",
      checkIn: "8:58 AM",
      scheduled: "9:00 AM",
    },
    {
      name: "Mike Chen",
      status: "Delayed",
      statusColor: "warning",
      location: "North Branch",
      checkIn: "9:12 AM",
      scheduled: "9:00 AM",
    },
    {
      name: "Emma Wilson",
      status: "On Time",
      statusColor: "success",
      location: "East Facility",
      checkIn: "9:00 AM",
      scheduled: "9:00 AM",
    },
    {
      name: "James Rodriguez",
      status: "On Time",
      statusColor: "success",
      location: "West Hub",
      checkIn: "8:55 AM",
      scheduled: "9:00 AM",
    },
    {
      name: "Lisa Anderson",
      status: "Late",
      statusColor: "error",
      location: "South Center",
      checkIn: "9:25 AM",
      scheduled: "9:00 AM",
    },
  ];

  return (
    <Box
      id="dashboard"
      sx={{
        py: { xs: 8, md: 12 },
        background:
          "linear-gradient(180deg, #0a0118 0%, #1a0b2e 50%, #0a0118 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Inject CSS animations */}
      <style>
        {floatingAnimation}
        {glowPulse}
      </style>

      {/* Animated Grid Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            linear-gradient(rgba(155, 93, 229, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(155, 93, 229, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
          opacity: 0.6,
          zIndex: 0,
        }}
      />

      {/* Floating Gradient Orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(155, 93, 229, 0.15) 0%, transparent 70%)",
          animation: "floatSlow 8s ease-in-out infinite",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(111, 45, 189, 0.12) 0%, transparent 70%)",
          animation: "floatSlow 10s ease-in-out infinite",
          animationDelay: "2s",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              color: "white",
            }}
          >
            Your Command Center
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
            }}
          >
            Everything you need in one powerful dashboard
          </Typography>
        </Box>

        {/* Dashboard Mockup */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Top Navigation */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "rgba(255, 255, 255, 0.1)",
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Tabs value={0} sx={{ minHeight: "auto" }}>
              <Tab label="Overview" sx={{ minHeight: "auto", py: 1 }} />
              <Tab label="Schedules" sx={{ minHeight: "auto", py: 1 }} />
              <Tab label="Staff" sx={{ minHeight: "auto", py: 1 }} />
              <Tab label="Reports" sx={{ minHeight: "auto", py: 1 }} />
              <Tab label="Settings" sx={{ minHeight: "auto", py: 1 }} />
            </Tabs>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip label="Today" size="small" />
              <Chip label="Week" size="small" variant="outlined" />
              <Chip label="Month" size="small" variant="outlined" />
              <Button
                startIcon={<FileDownloadIcon />}
                size="small"
                variant="outlined"
              >
                Export
              </Button>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 3 }}>
            {/* Top Row - Metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {metrics.map((metric, index) => (
                <Grid item xs={6} sm={6} md={3} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontWeight: 500,
                          color: "rgba(255, 255, 255, 0.7)",
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 0.5,
                          fontSize: { xs: "1.5rem", md: "2rem" },
                          color: "white",
                        }}
                      >
                        {metric.value}
                      </Typography>
                      {metric.change && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {metric.positive ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ fontSize: 16, color: "error.main" }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: metric.positive
                                ? "success.main"
                                : "error.main",
                              fontWeight: 600,
                            }}
                          >
                            {metric.change}
                          </Typography>
                        </Box>
                      )}
                      {metric.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {metric.subtitle}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Second Row - Charts */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Bar Chart Placeholder */}
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    p: 3,
                    height: 280,
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "white" }}
                  >
                    Check-in Patterns by Hour
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-around",
                      height: 180,
                      position: "relative",
                    }}
                  >
                    {[60, 85, 95, 70, 45].map((height, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: "15%",
                          height: `${height}%`,
                          background: theme.palette.gradient.main,
                          borderRadius: 1,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-around",
                      mt: 2,
                    }}
                  >
                    {["6AM", "9AM", "12PM", "3PM", "6PM"].map((time, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                      >
                        {time}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Donut Chart Placeholder */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    p: 3,
                    height: 280,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "white" }}
                  >
                    Staff Status
                  </Typography>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        background: `conic-gradient(
                          ${theme.palette.success.main} 0% 68%, 
                          ${theme.palette.warning.main} 68% 80%, 
                          ${theme.palette.grey[300]} 80% 100%
                        )`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: "50%",
                          background: "rgba(10, 1, 24, 0.9)",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: "success.main",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        >
                          On Time
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: "white" }}
                      >
                        68%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: "warning.main",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        >
                          Delayed
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: "white" }}
                      >
                        12%
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: "grey.300",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        >
                          Off
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: "white" }}
                      >
                        20%
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Third Row - Staff Activity Table */}
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "white" }}
                >
                  Staff Activity
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Staff Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Status
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Location
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Check-in
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Scheduled
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: "white" }}
                        >
                          Actions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffActivity.map((staff, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "white" }}
                          >
                            {staff.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={staff.status}
                            size="small"
                            color={staff.statusColor}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            {staff.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {staff.checkIn}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            {staff.scheduled}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ mr: 0.5, color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DashboardPreview;
